/**
 * Despacha para o renderer correto com base no mimeType do item.
 */
import { useEffect, useRef } from 'react'
import type { PlaybackResult, PlayQueueItem } from '@/types/player.types'
import { normalizeEmbedUrl } from '@/utils/embed-url'
import { ImageRenderer } from './image-renderer'
import { ScreenOffRenderer } from './screen-off-renderer'
import { VideoRenderer } from './video-renderer'
import { WebViewRenderer } from './web-view-renderer'

interface Props {
  item: PlayQueueItem
  onEnd: (result: PlaybackResult) => void
}

function isSkippable(item: PlayQueueItem): boolean {
  const { externalUrl, assetId, assetUrl, mimeType } = item
  if (item.eventAction === 'screen_off') return false
  // Item de URL externa — sempre renderizável
  if (externalUrl) return false
  // Asset sem URL resolvida — pular
  if (!assetId || !assetUrl) return true
  const isKnownMime =
    mimeType.startsWith('video/') ||
    mimeType.startsWith('image/') ||
    mimeType === 'text/html' ||
    mimeType.startsWith('application/')
  return !isKnownMime
}

export function ItemRenderer({ item, onEnd }: Props) {
  const skip = isSkippable(item)
  /** Uma vez por item “skip”; sobrevive a StrictMode remount com a mesma chave. */
  const skipHandledKeyRef = useRef<string | null>(null)

  // Avança imediatamente via useEffect para assets inválidos ou mime desconhecido,
  // evitando side effects no corpo do render.
  useEffect(() => {
    if (!skip) {
      skipHandledKeyRef.current = null
      return
    }
    const key = `${item.slotId}-${item.assetId ?? ''}-${item.externalUrl ?? ''}-${item.playlistItemId ?? ''}`
    if (skipHandledKeyRef.current === key) return
    skipHandledKeyRef.current = key
    // Item pulado não foi exibido → sem play log (F-07).
    onEnd({ played: false })
  }, [skip, onEnd, item.slotId, item.assetId, item.externalUrl, item.playlistItemId])

  if (skip) return null

  const { mimeType, assetId, assetUrl, externalUrl, durationSeconds, checksum } = item

  if (item.eventAction === 'screen_off') {
    return <ScreenOffRenderer key={item.slotId} durationSeconds={durationSeconds} onEnd={onEnd} />
  }

  if (externalUrl) {
    const embedUrl = normalizeEmbedUrl(externalUrl)
    return <WebViewRenderer key={embedUrl} url={embedUrl} durationSeconds={durationSeconds} onEnd={onEnd} />
  }

  if (mimeType.startsWith('video/')) {
    return (
      <VideoRenderer
        assetId={assetId as string}
        assetUrl={assetUrl as string}
        hlsUrl={item.hlsUrl}
        checksum={checksum}
        durationSeconds={durationSeconds}
        onEnd={onEnd}
      />
    )
  }

  if (mimeType.startsWith('image/')) {
    return (
      <ImageRenderer
        assetId={assetId as string}
        assetUrl={assetUrl as string}
        checksum={checksum}
        durationSeconds={durationSeconds}
        onEnd={onEnd}
      />
    )
  }

  return (
    <WebViewRenderer
      key={assetUrl as string}
      url={assetUrl as string}
      durationSeconds={durationSeconds}
      onEnd={onEnd}
    />
  )
}
